/**
 * Pre-queue setup contracts shared between frontend, server-php and server-node.
 *
 * - B08: state machine, HMAC ready token, device persistence, virtual camera
 *   blocklist. See `openspec/changes/pre-queue-setup-devices/` for full spec.
 *
 * NOTE: types only — runtime constants live alongside.
 */

// --- State machine states ---------------------------------------------------

/**
 * Discrete states of the pre-queue setup orchestrator. Guarded transitions
 * enforced by `prequeueStore`. See `PREQUEUE_TRANSITIONS` table.
 */
export type PrequeueState =
  | 'idle'
  | 'requesting-permission'
  | 'permission-denied'
  | 'enumerating'
  | 'enumeration-failed'
  | 'picking'
  | 'validating'
  | 'virtual-camera-blocked'
  | 'ready'
  | 'enqueueing'
  | 'enqueued';

/** Single transition audit entry, retained in `prequeueStore.history`. */
export interface PrequeueEvent {
  /** ISO-8601 timestamp with millisecond precision. */
  at: string;
  from: PrequeueState;
  to: PrequeueState;
  /** Optional reason (e.g., `permission_denied`, `virtual_camera_detected`). */
  reason?: string;
}

// --- HMAC ready token -------------------------------------------------------

/**
 * Token issued by `POST /api/v1/prequeue/ready` after pre-queue setup passes.
 * Carried by the frontend in `match_request` WS payload; server-node verifies
 * HMAC, expiry, fingerprint binding, single-use replay.
 */
export interface PrequeueReadyToken {
  /** Opaque `base64url(payload).base64url(signature)`. */
  token: string;
  /** Unix epoch seconds when the token expires (TTL 600s = 10 min). */
  exp: number;
}

// --- Device enumeration -----------------------------------------------------

export type DeviceMediaType = 'camera' | 'microphone' | 'speaker';

/** Per-device row payload for `POST /api/v1/devices/enumerate`. */
export interface DeviceEnumerationEntry {
  media_type: DeviceMediaType;
  device_id_browser: string;
  /** Browser may return empty before permission grant (CF-2814). */
  label?: string;
}

/** `GET /api/v1/devices/last-used` response data shape. */
export interface DevicesLastUsedResponse {
  camera: LastUsedDevice | null;
  microphone: LastUsedDevice | null;
  speaker: LastUsedDevice | null;
}

export interface LastUsedDevice {
  device_id_browser: string;
  label: string | null;
  last_seen_at: string;
  /**
   * `same` — last_used row matches the query fingerprint (current device).
   * `cross` — fallback from another device (UI shows "last used elsewhere").
   */
  fingerprint_origin: 'same' | 'cross';
}

// --- Webcam blocklist -------------------------------------------------------

export type WebcamPatternType = 'exact' | 'regex';
export type WebcamSeverity = 'low' | 'medium' | 'high';

/** Single blocklist row served by `GET /api/v1/webcam/blocklist`. */
export interface WebcamBlocklistEntry {
  id: number;
  pattern_type: WebcamPatternType;
  pattern: string;
  label: string;
  severity: WebcamSeverity;
  /** Optional admin override `{ Guest: 'block_pairing', Premium: 'audit_only', ... }`. */
  action_per_tier?: Record<string, WebcamAction> | null;
}

/**
 * Action taken when a virtual camera is detected. Resolved per-tier via the
 * priority chain in `pre-queue-setup-devices/specs/webcam-validation`.
 */
export type WebcamAction = 'block_pairing' | 'warn_peer' | 'audit_only';

/**
 * Fallback enum (last step in resolution chain). Keys are lowercase `tier_key`
 * matching `subscription_tiers.tier_key` DB column (migration 0001). Frontend
 * MUST pass `tier_key` from `/api/v1/auth/me` directly — NEVER derive from
 * `tier_id` integer via client-side mapping (root cause of the Guest-not-blocked
 * bug observed during B08 smoke test 2026-05-17).
 */
export const WEBCAM_DEFAULT_ACTION_BY_TIER: Readonly<Record<string, WebcamAction>> = {
  guest: 'block_pairing',
  regular: 'warn_peer',
  premium: 'audit_only',
  vip: 'audit_only',
  admin: 'audit_only',
};

// --- WS error code ----------------------------------------------------------

/**
 * Server-node rejects `match_request` without a valid `prequeue_ready_token`
 * with `WSError { code: 'prequeue_not_ready' }`. Frontend handler redirects to
 * `/app/setup`.
 */
export const PREQUEUE_NOT_READY_CODE = 'prequeue_not_ready' as const;

// --- Tokens & TTLs ----------------------------------------------------------

/** TTL applied to `prequeue_ready_token` issued by PHP. */
export const PREQUEUE_READY_TOKEN_TTL_SECONDS = 600;

/** Frontend localStorage key prefix for last-used device per fingerprint. */
export const PREQUEUE_LS_CAMERA_PREFIX = 'prequeue:lastCamera:';
export const PREQUEUE_LS_MIC_PREFIX = 'prequeue:lastMic:';

/** Frontend ring-buffer cap for state-transition audit history. */
export const PREQUEUE_HISTORY_RING_SIZE = 50;
